/**
 * The front door. Two things: start a room, or join one (SPEC §5.1).
 *
 * Creating a room goes through `POST /api/rooms`, which mints a code, re-rolls
 * on collision or blocklist hit, and **claims the Durable Object** before
 * answering. Minting a code on the client would skip the claim and let two
 * simultaneous hosts race into the same room.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MAX_PLAYERS, MIN_PLAYERS } from '@idgame/protocol';
import { createRoom } from '../../lib/roomCode.js';
import { listSessions, saveHostToken } from '../../net/session.js';
import { Button, Notice, Screen } from '../../app/ui.js';
import { HowToPlayButton } from '../../components/HowToPlay.js';

export function LandingScreen() {
  const navigate = useNavigate();
  const [recent] = listSessions();
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const start = async () => {
    if (creating) return;
    setCreating(true);
    setProblem(null);
    try {
      const { roomCode, hostToken } = await createRoom();
      // Stored before navigating, because the creator is about to spend a
      // minute on a name and a selfie while the people they invited join ahead
      // of them — this is what makes them the host anyway (SPEC §5.5).
      if (hostToken) saveHostToken(roomCode, hostToken);
      navigate(`/r/${roomCode}`);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "We couldn't start a room.");
      setCreating(false);
    }
  };

  return (
    <Screen
      eyebrow="Same room. Phones out."
      // The rules sit behind the `i` in the corner, not on the front door: the
      // page still opens on the wordmark and two buttons.
      meta={<HowToPlayButton />}
      testId="landing"
      footer={
        <div className="flex flex-col gap-2.5">
          {problem ? <Notice tone="warn">{problem}</Notice> : null}
          {recent ? (
            <Button variant="secondary" onClick={() => navigate(`/r/${recent.roomCode}`)}>
              Rejoin room {recent.roomCode}
            </Button>
          ) : null}
          <Button onClick={() => void start()} disabled={creating}>
            {creating ? 'Starting…' : 'Start a room'}
          </Button>
          <Button variant="quiet" onClick={() => navigate('/join')}>
            Join with a code
          </Button>
        </div>
      }
    >
      <div className="flex flex-1 flex-col justify-center gap-4">
        {/*
          The logo carries the wordmark, so it *is* the heading — an `h1` with
          the type set again underneath would be the same three words twice, and
          a bare `img` would leave the front door with no heading at all. The
          alt text is what a screen reader reads as the h1.

          Width and height are on the tag because this is the first paint on the
          slowest connection in the room: without them the button ladder below
          jumps down the moment the file lands.

          `id-game-wordmark.png` rather than the master in the same folder: the
          master carries ~165px of transparent margin on every side, which CSS
          cannot see and therefore cannot lay out around — the mark sat small in
          a box the size of the column with a band of nothing under it. This one
          is the same artwork trimmed to its own edges and cut to display size,
          so the spacing here is the spacing on screen.
        */}
        <h1>
          <img
            src="/brand/id-game-wordmark.png"
            alt="The ID Game"
            width={880}
            height={298}
            decoding="async"
            className="h-auto w-full"
          />
        </h1>
        <p className="max-w-[17rem] text-[0.8125rem] leading-relaxed text-ink-soft">
          {MIN_PLAYERS}–{MAX_PLAYERS} players. One of you ranks the room; everyone else bets on who
          lands top and bottom. Then you explain yourself out loud.
        </p>
      </div>
    </Screen>
  );
}
